/**
 * Per-route feedback-context registry (ADR-0074 §3.2).
 *
 * Route components call `setFeedbackContext({ kind, id, displayName })` on
 * mount and `clearFeedbackContext()` on unmount. The popover reads via
 * `readCurrentContext()` once when it opens (`useState` initializer) and
 * SUBMITS THAT SNAPSHOT — switching surfaces while the popover is open
 * must not change what gets reported.
 *
 * Module-scoped (single latest entry). Replacing the entry overwrites
 * the prior one; clearing only takes effect if the current entry is the
 * one being cleared (so a fast unmount → mount → unmount sequence on
 * sibling routes does not blow away the new mount's context).
 */

export type FeedbackContextKind = "pipeline" | "node" | "run" | "action_item" | "query";

export interface FeedbackContextEntry {
  kind: FeedbackContextKind;
  id: string;
  displayName: string | null;
}

let current: FeedbackContextEntry | null = null;

export function setFeedbackContext(entry: FeedbackContextEntry): void {
  current = { ...entry };
}

/**
 * Clear the registry. If `match` is supplied, only clears when the current
 * entry matches kind+id — useful for unmount handlers that should not
 * stomp a sibling route's mount that fired first.
 */
export function clearFeedbackContext(match?: { kind: FeedbackContextKind; id: string }): void {
  if (!match) {
    current = null;
    return;
  }
  if (current && current.kind === match.kind && current.id === match.id) {
    current = null;
  }
}

export function readCurrentContext(): FeedbackContextEntry | null {
  return current ? { ...current } : null;
}

/** Test-only: reset state. */
export function _resetForTests(): void {
  current = null;
}
